import { useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { api } from '../../services/api.js';
import { downloadCsv } from '../../utils/downloadCsv.js';
import { TRIP_PURPOSES, purposeLabel } from '../../utils/tripLabels.js';
import { type TripsExportRow, tripsToCsv } from './tripsCsv.js';
import type { TripCounts } from './types.js';

/**
 * 361 (#980, design 1h): the month's rentals by the customers' trip answers, and a CSV of any date range for the PO's
 * analysis. Counted over the same rentals as the month's "Rentals" figure; the export's paid rows agree (parity test).
 */
const pct = (part: number, whole: number) =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

function lastDay(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${month}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, '0')}`;
}

export function TripsCard({
  trips,
  month,
  wide = false,
}: {
  trips: TripCounts;
  month: string;
  /** 422 v2 (design frames 1-4): full width on the platform's month, its three parts side by side from `lg`. */
  wide?: boolean;
}) {
  const [from, setFrom] = useState(`${month}-01`);
  const [to, setTo] = useState(lastDay(month));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const purposes = [...TRIP_PURPOSES]
    .map((p) => ({ key: p as string, label: purposeLabel(p), n: trips.by_purpose[p] }))
    .sort((a, b) => b.n - a.n)
    .concat([{ key: 'none', label: 'Not answered', n: trips.by_purpose.none }]);
  const most = Math.max(1, ...purposes.map((p) => p.n));

  async function exportCsv() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.get<{ rows: TripsExportRow[]; truncated: boolean }>(
        `/dashboard/trips-export?from=${from}&to=${to}`,
      );
      downloadCsv(`trips-${from}-${to}.csv`, tripsToCsv(res.rows));
      if (res.truncated)
        setError('Only the first 5,000 bookings were exported. Choose a shorter range.');
    } catch (err) {
      setError((err as Error).message || 'The export failed. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      padding="none"
      className={`flex flex-col gap-3.5 p-4 ${wide ? 'dark:border-hairline-strong dark:bg-surface' : ''}`}
      data-testid="trips-card"
      data-wide={wide ? 'true' : undefined}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3
          data-testid="trips-rentals"
          className="text-[11px] font-semibold tracking-wider text-steel uppercase dark:text-slate"
        >
          Trips this month · {trips.rentals} {trips.rentals === 1 ? 'rental' : 'rentals'}
        </h3>
      </div>
      <div
        data-testid="trips-parts"
        className={
          wide
            ? 'grid gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)_minmax(0,1fr)]'
            : 'flex flex-col gap-3.5'
        }
      >
        <div className="grid grid-cols-2 content-start gap-3">
          <div>
            <div
              data-testid="trips-share-delivery"
              className="text-[28px] font-bold text-ink tabular-nums"
            >
              {pct(trips.food_delivery, trips.rentals)}
            </div>
            <div
              data-testid="trips-share-delivery-label"
              className="text-xs text-steel dark:text-slate"
            >
              OK to do food delivery ({trips.food_delivery})
            </div>
          </div>
          <div>
            <div
              data-testid="trips-share-non-halal"
              className="text-[28px] font-bold text-ink tabular-nums"
            >
              {pct(trips.non_halal, trips.food_delivery)}
            </div>
            <div
              data-testid="trips-share-non-halal-label"
              className="text-xs text-steel dark:text-slate"
            >
              of them OK with non-halal ({trips.non_halal})
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3.5">
          <h4 className="text-[11px] font-semibold tracking-wider text-steel uppercase dark:text-slate">
            Purpose
          </h4>
          <div className="flex flex-col gap-2">
            {purposes.map((p) => (
              <div
                key={p.key}
                data-testid="trips-purpose"
                className="grid grid-cols-[140px_minmax(0,1fr)_34px] items-center gap-2.5 text-sm"
              >
                <span className={p.key === 'none' ? 'text-steel dark:text-slate' : 'text-ink'}>
                  {p.label}
                </span>
                <div
                  className="h-2 overflow-hidden rounded-full bg-hairline-soft"
                  aria-hidden="true"
                >
                  <div
                    className={`h-full rounded-full ${p.key === 'none' ? 'bg-hairline-strong' : 'bg-primary'}`}
                    style={{ width: `${(p.n / most) * 100}%` }}
                  />
                </div>
                <span className="text-right text-slate tabular-nums">{p.n}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-3.5">
          <h4 className="text-[11px] font-semibold tracking-wider text-steel uppercase dark:text-slate">
            Students in the car
          </h4>
          <div className="grid grid-cols-6 gap-2 text-center text-xs">
            {(['1', '2', '3', '4', '5', 'none'] as const).map((k) => (
              <div
                key={k}
                data-testid="trips-students"
                className={k === 'none' ? 'text-steel dark:text-slate' : 'text-slate'}
              >
                <div className="text-xl font-bold text-ink tabular-nums">
                  {trips.by_students[k]}
                </div>
                {k === 'none' ? 'n/a' : k}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-2 border-t border-hairline-soft pt-3">
        <label className="flex flex-col gap-1 text-xs text-steel dark:text-slate">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-9 rounded-md border border-hairline-strong bg-canvas px-2 text-sm text-ink"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-steel dark:text-slate">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-9 rounded-md border border-hairline-strong bg-canvas px-2 text-sm text-ink"
          />
        </label>
        <Button type="button" variant="secondary" size="sm" loading={busy} onClick={exportCsv}>
          Export CSV
        </Button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </Card>
  );
}
